import { css } from 'aphrodite';
import { parseAsString, useQueryState } from 'nuqs';
import * as React from 'react';
import { useSupportCategoriesQuery } from '../../../../queries/Support/useSupportCategories';
import { useSupportReportOverviewQuery } from '../../../../queries/Support/useSupportReportOverviewQuery';
import { useSupportTeamPresentation } from '../../../../queries/Support/useSupportTeam';
import { Button } from '../../../../web/components-v2/Button';
import { DateRangePicker } from '../../../../web/components-v2/DateRangePicker';
import { inboxStyles as s } from '../styles/inboxStyles';
import { AssistantImpactCard } from './AssistantImpactCard';
import { CategoryVolumeCard } from './CategoryVolumeCard';
import { FirstResponseCard } from './FirstResponseCard';
import { ReportStatTile } from './ReportStatTile';
import {
	alignVolumeSeries,
	buildCategoryRows,
	buildFirstResponseRows,
	buildReportCsv,
	buildReportFileName,
	fillDailySeries,
	formatCount,
	formatCsatScore,
	formatDurationMinutes,
	formatReportRangeLabel,
	getAssistantStats,
	getDefaultReportDateRange,
	getDelta,
	getNetDelta,
	getPreviousWindow,
	getReportRangeError,
	getReportWindow,
	getScoreDelta,
	getTopAgents,
	type ReportDateRange,
} from './reportsPresentation';
import { reportsStyles as rs } from './styles/reportsStyles';
import { TopAgentsCard } from './TopAgentsCard';
import { VolumeTrendCard } from './VolumeTrendCard';

interface SupportReportsScreenProps {
	/** null with `allMailboxes` scopes the report to every mailbox the caller can read. */
	mailboxId: string | null;
	mailboxName?: string;
	allMailboxes?: boolean;
}

export const SupportReportsScreen: React.FC<SupportReportsScreenProps> = ({ mailboxId, mailboxName, allMailboxes }) => {
	const [startDate, setStartDate] = useQueryState('startDate', parseAsString.withDefault(''));
	const [endDate, setEndDate] = useQueryState('endDate', parseAsString.withDefault(''));
	const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const range: ReportDateRange = startDate && endDate ? { startDate, endDate } : getDefaultReportDateRange();
	const rangeError = getReportRangeError(range);
	const requestedWindow = rangeError ? null : getReportWindow(range);
	const rangeLabel = formatReportRangeLabel(range);
	const scopeName = allMailboxes ? 'All mailboxes' : mailboxName;

	const overviewQuery = useSupportReportOverviewQuery({
		mailboxId,
		// The hook treats an empty bound as "nothing to ask for", which is what an unusable range is.
		start: requestedWindow?.start ?? '',
		end: requestedWindow?.end ?? '',
		timezone,
	});
	// The all-mailboxes view has no single mailbox to load categories/team for: the API resolves
	// category names, and agent avatars fall back to initials.
	const categoriesQuery = useSupportCategoriesQuery(mailboxId ?? '');
	const team = useSupportTeamPresentation(mailboxId ?? '');

	const overview = overviewQuery.data;
	const categories = categoriesQuery.data?.values ?? [];
	// placeholderData keeps the previous range rendered, so skeletons only show on the first load.
	// While the hold shows another window's (or mailbox's) data, dim the body and block Export so
	// stale numbers are never read — or saved — under the new labels.
	const isLoading = overviewQuery.isPending;
	const isStale = overviewQuery.isPlaceholderData;
	const isError = overviewQuery.isError && !overview;

	// The picker sets and clears both bounds together, so anything short of a full range drops back
	// to the default window rather than leaving a half-filtered report.
	const handleRangeChange = (from: string, to: string) => {
		const hasRange = Boolean(from && to);
		void setStartDate(hasRange ? from : null);
		void setEndDate(hasRange ? to : null);
	};

	const handleExport = () => {
		if (!overview || isStale) return;
		const blob = new Blob([buildReportCsv(overview, categories)], { type: 'text/csv;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = buildReportFileName(scopeName, range);
		anchor.click();
		URL.revokeObjectURL(url);
	};

	// Fill against the window the API actually reported on, which is authoritative for bucketing.
	const volumeSeries = overview
		? alignVolumeSeries(
				fillDailySeries({ start: overview.start, end: overview.end }, overview.volume.createdByDay),
				fillDailySeries(
					getPreviousWindow({ start: overview.start, end: overview.end }),
					overview.volume.createdByDayPrevious
				)
			)
		: [];

	return (
		<div className={css(s.reportsScreen)}>
			<div className={css(s.reportsInner)}>
				<header className={css(rs.header)}>
					<div className={css(rs.headerText)}>
						<h1 className={css(s.reportsTitle)}>Support Reports</h1>
						<div className={css(rs.subtitle)}>
							{scopeName ? `${scopeName} · ` : ''}
							{rangeLabel}
						</div>
					</div>
					<div className={css(rs.headerActions)}>
						<DateRangePicker
							ariaLabel='Report date range'
							size='sm'
							showOutsideDays={false}
							from={range.startDate}
							to={range.endDate}
							onChange={handleRangeChange}
						/>
						<Button
							type='button'
							variant='secondary'
							disabled={!overview || isStale || !!rangeError}
							onClick={handleExport}
						>
							Export
						</Button>
					</div>
				</header>

				{rangeError ? (
					<div role='alert' className={css(rs.errorCard)}>
						{rangeError}
					</div>
				) : isError ? (
					<div className={css(rs.errorCard)}>Failed to load support reports.</div>
				) : (
					<div className={css(rs.content, isStale && rs.contentRefreshing)} aria-busy={isStale}>
						{overview && overview.warnings.length > 0 && (
							<div role='alert' className={css(rs.card)}>
								<p>This report is incomplete. Some figures may be missing or based on limited data.</p>
								<ul>
									{overview.warnings.map(warning => (
										<li key={warning}>{warning}</li>
									))}
								</ul>
							</div>
						)}
						<AssistantImpactCard
							stats={overview ? getAssistantStats(overview.assistant, overview.volume.created) : undefined}
							rangeLabel={rangeLabel}
							isLoading={isLoading}
						/>

						<div className={css(rs.kpiGrid)}>
							<ReportStatTile
								label='Conversations'
								value={overview ? formatCount(overview.volume.created) : '—'}
								delta={overview ? getDelta(overview.volume.created, overview.volume.createdPrevious) : null}
								isLoading={isLoading}
							/>
							<ReportStatTile
								label={overview ? `CSAT · ${formatCount(overview.csat.responseCount)} ratings` : 'CSAT'}
								value={overview ? formatCsatScore(overview.csat.score) : '—'}
								delta={overview ? getScoreDelta(overview.csat.score, overview.csat.scorePrevious) : null}
								isLoading={isLoading}
							/>
							<ReportStatTile
								label='Median first response'
								value={overview ? formatDurationMinutes(overview.firstResponse.medianMinutes) : '—'}
								delta={
									overview
										? getDelta(overview.firstResponse.medianMinutes, overview.firstResponse.medianMinutesPrevious, true)
										: null
								}
								isLoading={isLoading}
							/>
							<ReportStatTile
								label='Median resolution'
								value={overview ? formatDurationMinutes(overview.resolution.medianMinutes) : '—'}
								delta={
									overview
										? getDelta(overview.resolution.medianMinutes, overview.resolution.medianMinutesPrevious, true)
										: null
								}
								isLoading={isLoading}
							/>
							<ReportStatTile
								label='Open backlog'
								value={overview ? formatCount(overview.volume.openBacklog) : '—'}
								delta={overview ? getNetDelta(overview.volume.created, overview.volume.closed) : null}
								isLoading={isLoading}
							/>
						</div>

						<div className={css(rs.splitRow)}>
							<VolumeTrendCard data={volumeSeries} isLoading={isLoading} />
							<FirstResponseCard
								medianMinutes={overview?.firstResponse.medianMinutes ?? null}
								measuredCount={overview?.firstResponse.measuredTicketCount ?? 0}
								rows={overview ? buildFirstResponseRows(overview.firstResponse.buckets) : []}
								isLoading={isLoading}
							/>
						</div>

						<div className={css(rs.splitRow)}>
							<CategoryVolumeCard
								rows={overview ? buildCategoryRows(overview.categories, categories) : []}
								isLoading={isLoading}
							/>
							<TopAgentsCard
								agents={overview ? getTopAgents(overview.agents) : []}
								profilePicMap={team.agentProfilePicMap}
								isLoading={isLoading}
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
